import type { TypesenseSearchParams } from '~/utils/typesenseClient'
import {
  buildTypesenseGeoSort,
  capSortFields,
  type GeoCoords,
} from '~/utils/typesenseGeo'
import {
  getEventListIndexName,
  getBoxesIndexName,
} from '~/utils/searchEngineHelpers'

/**
 * Typesense query builders for the curated event showcases (KFT-4116): homepage,
 * category promotions, and review-success recommendations. These replace the
 * per-showcase Algolia replica-index queries that geo-biased via
 * `aroundLatLngViaIP`; on the Typesense path proximity comes from an explicit
 * `_geoloc(lat,lng)` sort fed by the viewer's coordinates (`useUserGeo()`).
 *
 * All builders are pure and engine-agnostic-safe: with no coords they emit the
 * same query minus the geo term, so every surface still renders without geo.
 *
 * Ranking: Algolia puts geo/proximity in the Textual ranking chain, *before*
 * `custom`, so proximity leads and each index's customRanking breaks ties.
 * Typesense has no replica indices, so each showcase queries the base
 * `events`/`products` collection with `_geoloc(...):asc` (bucketed by
 * GEO_SORT_PRECISION_KM) followed by that index's customRanking, ported 1:1
 * from Algolia. Kits/boxes carry no location, so they are never geo-sorted.
 *
 * `has_events` is a *sort* term only, never a filter: events without upcoming
 * dates should sink to the bottom, not be excluded — a short showcase is better
 * than an empty one. (The Algolia path still filters `has_events`; unchanged.)
 */

// Curated showcases search with `q: '*'` (no text query), so `query_by` is
// required by Typesense but never actually scored.
const SHOWCASE_QUERY_BY = 'title'

// Algolia customRanking per index, ported 1:1 — `has_events:desc` ranks
// dated events first without excluding the rest. See the index configs:
//   events_de / events_de_top_sold_events / promotions (base events_de)
const EVENTS_BASE_RANKING =
  'has_events:desc,trending.90_days:desc,has_promotions:desc'
//   events_de_sort_by_created_at_desc (created_at is a custom attr, below geo)
const NEWEST_RANKING = `created_at:desc,${EVENTS_BASE_RANKING}`
//   events_de_top_gift_ideas
const GIFT_IDEAS_RANKING =
  'has_events:desc,total_giftcards_sold:desc,has_promotions:desc'
//   products_de (kits) — location-agnostic, no geo
const BOXES_RANKING = 'total_tickets_sold:desc,opt_score:desc'

/**
 * Geo-leading showcase sort: nearest-first (10km bucket), then the index's
 * customRanking. Falls back to the customRanking alone with no coords (SSR /
 * geo unavailable). Both paths are capped to Typesense's 3 sort-field limit,
 * so geo + the two leading customRanking terms survive (trailing terms like
 * has_promotions drop when geo is present).
 */
const geoLeadingSort = (
  coords: GeoCoords | null | undefined,
  ranking: string
): string => buildTypesenseGeoSort(coords, ranking) ?? capSortFields(ranking)

export interface HomepageTypesenseQueryInput {
  locale: string
  coords?: GeoCoords | null
  /** Unix seconds — start of the "next 7 days" window for the top-sold showcase. */
  todayTs: number
  /** Unix seconds — end of that window. */
  weekEndTs: number
  hitsPerShowcase: number
}

/**
 * The homepage's five showcases, in the same order as the Algolia
 * `homePageQueryParams`: upcoming top-sold (next 7 days), newest, top gift ideas
 * (double width), promotions, and kits/boxes.
 *
 * Boxes are nationwide / location-agnostic (matching the federated lab), so they
 * are never geo-sorted.
 */
export const buildHomepageTypesenseQueries = ({
  locale,
  coords,
  todayTs,
  weekEndTs,
  hitsPerShowcase,
}: HomepageTypesenseQueryInput): TypesenseSearchParams[] => {
  const events = getEventListIndexName(locale)
  const boxes = getBoxesIndexName(locale)
  const dateWindow = `(dates_ts:[${todayTs}..${weekEndTs}] || dates_private_ts:[${todayTs}..${weekEndTs}])`

  return [
    {
      // events_de_top_sold_events (same customRanking as events_de).
      // Only the upcoming-dates window is a hard filter; has_events ranks (sort).
      collection: events,
      q: '*',
      query_by: SHOWCASE_QUERY_BY,
      per_page: hitsPerShowcase,
      filter_by: dateWindow,
      sort_by: geoLeadingSort(coords, EVENTS_BASE_RANKING),
    },
    {
      // events_de_sort_by_created_at_desc — no filter, ranked only.
      collection: events,
      q: '*',
      query_by: SHOWCASE_QUERY_BY,
      per_page: hitsPerShowcase,
      sort_by: geoLeadingSort(coords, NEWEST_RANKING),
    },
    {
      // events_de_top_gift_ideas — no filter, ranked only.
      collection: events,
      q: '*',
      query_by: SHOWCASE_QUERY_BY,
      per_page: hitsPerShowcase * 2,
      sort_by: geoLeadingSort(coords, GIFT_IDEAS_RANKING),
    },
    {
      // events_de base — promotions is the only hard filter.
      collection: events,
      q: '*',
      query_by: SHOWCASE_QUERY_BY,
      per_page: hitsPerShowcase,
      filter_by: 'has_promotions:=true',
      sort_by: geoLeadingSort(coords, EVENTS_BASE_RANKING),
    },
    {
      // products_de (kits) — location-agnostic, never geo-sorted.
      collection: boxes,
      q: '*',
      query_by: SHOWCASE_QUERY_BY,
      per_page: hitsPerShowcase,
      sort_by: BOXES_RANKING,
    },
  ]
}

export interface ShowcaseEventsTypesenseQueryInput {
  locale: string
  coords?: GeoCoords | null
  /** Category name facet, if the showcase is scoped to one. */
  category?: string
  hitsPerPage: number
}

/**
 * Category promotions showcase (usePromotionEvents): promoted, bookable events
 * near the viewer, optionally scoped to a category.
 */
export const buildPromotionTypesenseQuery = ({
  locale,
  coords,
  category,
  hitsPerPage,
}: ShowcaseEventsTypesenseQueryInput): TypesenseSearchParams => ({
  collection: getEventListIndexName(locale),
  q: '*',
  query_by: SHOWCASE_QUERY_BY,
  per_page: hitsPerPage,
  filter_by: [
    'has_promotions:=true',
    category ? `categories.name:=[${category}]` : '',
  ]
    .filter(Boolean)
    .join(' && '),
  sort_by: geoLeadingSort(coords, EVENTS_BASE_RANKING),
})

/**
 * Review-success recommendations (useReviewRecommendedEvents): bookable events
 * near the buyer in the category they just reviewed. The category facet is
 * dropped when absent, so a purely proximity-ranked list still renders.
 */
export const buildReviewRecommendedTypesenseQuery = ({
  locale,
  coords,
  category,
  hitsPerPage,
}: ShowcaseEventsTypesenseQueryInput): TypesenseSearchParams => ({
  collection: getEventListIndexName(locale),
  q: '*',
  query_by: SHOWCASE_QUERY_BY,
  per_page: hitsPerPage,
  // Category is the only optional hard filter; no has_events filter.
  ...(category ? { filter_by: `categories.name:=[${category}]` } : {}),
  sort_by: geoLeadingSort(coords, EVENTS_BASE_RANKING),
})
